import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
  HttpResponse
} from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { concatMap, delay, retryWhen, switchMap, tap } from 'rxjs/operators';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { MessageService } from '../messages/message.service';
import { AuthService } from './auth/auth.service';
import { AppConfig } from '../appconfig';
import { Router } from '@angular/router';
import { AppUrls } from './app-urls';

export const retryCount = 3;
export const retryWaitMilliSeconds = 1000;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly authStorage = inject(OAuthStorage);
  private readonly messageService = inject(MessageService);
  private readonly appConfig = inject(AppConfig);
  private readonly router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(this.setHeaders(request)).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const messages = event.headers.get('message-count');
          if (messages) {
            this.messageService.messageCount = Number(messages);
          }
        }
      }),
      retryWhen((error) =>
        error.pipe(
          concatMap((error, count) => {
            // authentication failed, no need to retry
            if (error.status === HttpStatusCode.Unauthorized) {
              return this.reAuthenticate().pipe(switchMap(() => next.handle(request)));
            }
            if (error.status === HttpStatusCode.Conflict) {
              this.authService.logoutWithError();
              return throwError(() => error);
            }
            // no content - is no error
            if (error.status === HttpStatusCode.NoContent) {
              return throwError(() => error);
            }
            if (error.status === HttpStatusCode.BadRequest) {
              return throwError(() => error);
            }
            if (error.status === HttpStatusCode.Forbidden) {
              return throwError(() => error);
            }

            // cross origin errors have status code 0
            if (error.status === 0) {
              return throwError(() => error);
            }

            if (count <= retryCount) {
              return of(error);
            }
            return throwError(() => error);
          }),
          delay(retryWaitMilliSeconds)
        )
      )
    );
  }

  private setHeaders(request: HttpRequest<any>): HttpRequest<any> {
    try {
      let headers = request.headers;

      if (AppUrls.urlMatchIdentity(request.url)) {
        return request;
      }

      // get the token from a service
      const accessToken = this.authStorage.getItem('access_token');
      const idToken = this.authStorage.getItem('id_token');

      // add token if we have one
      if (accessToken) {
        headers = headers.set('Authorization', 'Bearer ' + accessToken);
      }
      if (idToken) {
        headers = headers.set('x-id', idToken);
      }
      // setting the accept header
      headers = headers.set('Accept', 'application/json');

      // if this is a login-request the header is
      // already set to x/www/formurl/encoded.
      // so if we already have a content-type, do not
      // set it, but if we don't have one, set it to
      // default --> json
      if (!headers.has('Content-Type')) {
        headers = headers.set('Content-Type', 'application/json');
      }

      if (headers.get('Content-Type') === 'form-data') {
        headers = headers.delete('Content-Type');
      }

      return request.clone({ headers });
    } catch (error) {
      console.error('Error in TokenInterceptor:', error);
      return request;
    }
  }

  reAuthenticate(): Observable<any> {
    console.log('reauthenticate');

    if (this.appConfig.identityShowLogin()) {
      return from(this.router.navigate(['/login']));
    }

    return from(this.authService.refresh());

    // this.authService.relogin(window.location.href);
    // return of();
  }

  // Response Interceptor
  private checkError(error: HttpErrorResponse): Observable<any> {
    if (error.status === 401) {
      // auto logout if 401 response returned from api
      // this.accountService.logout();
    }
    const err = error.error.message || error.statusText;
    return throwError(err);
  }
}
